/**
 * altitudeDisplay — how a person reads an altitude.
 *
 * In the Game an altitude is a number, 1 to 5. A department's Method may name its levels, and those
 * names are tags that follow the number: "Altitude 3 · Feature" (owner ruling 2026-10-28, the words
 * chosen: "Altitude levels are numbers, 1 to 5, in the Game; a Method may name its levels, and those
 * names are tags").
 *
 * Storage is not touched. An item stores `altitude_level` (1–5), and older rows also carry the named
 * `item_altitude` enum (theme, initiative, feature, task, subtask, outcome, epic, story). This file is
 * the one place that turns a stored value into the number (`altitudeNumberOf`, which reads the one
 * legacy mapping, LEGACY_ALTITUDE_TO_LEVEL) and the number into the words a person reads.
 *
 * The tag, in order:
 *   1. the label the workspace gave this category for the level (Settings › Item Categories);
 *   2. the name this category's built-in cell carries for the level (ALTITUDE_NAMES_BY_CATEGORY);
 *   3. the stored name, capitalized, when the stored value is a name (a legacy `item_altitude` value);
 *   4. the generic name for the level (ALTITUDE_LEVEL_NAMES).
 */

import {
  ALTITUDE_LEVEL_NAMES,
  ALTITUDE_NAMES_BY_CATEGORY,
  LEGACY_ALTITUDE_TO_LEVEL,
  type AltitudeLevel,
  type ItemAltitude,
} from '../types/prioritization/itemLifecycle';

/** The labels a workspace gave one category, by level. Absent or empty means "use the built-in name". */
export type AltitudeLabelSet = Partial<Record<AltitudeLevel, string | null>> | null | undefined;

const LEVELS: readonly AltitudeLevel[] = [1, 2, 3, 4, 5];

function isLevel(n: number): n is AltitudeLevel {
  return Number.isInteger(n) && n >= 1 && n <= 5;
}

function legacyLevelOf(name: string): AltitudeLevel | null {
  return Object.prototype.hasOwnProperty.call(LEGACY_ALTITUDE_TO_LEVEL, name)
    ? LEGACY_ALTITUDE_TO_LEVEL[name as ItemAltitude]
    : null;
}

/**
 * The number 1–5 for a stored altitude: the number itself, a numeric string ("3"), or a legacy
 * `item_altitude` name. A name with no level (theme, subtask: the backfill in migration 046 left
 * them without one) and anything unrecognized has no number: null.
 */
export function altitudeNumberOf(stored: unknown): AltitudeLevel | null {
  if (typeof stored === 'number') return isLevel(stored) ? stored : null;
  if (typeof stored !== 'string') return null;
  const text = stored.trim().toLowerCase();
  if (!text) return null;
  if (/^\d+$/.test(text)) return isLevel(Number(text)) ? (Number(text) as AltitudeLevel) : null;
  return legacyLevelOf(text);
}

/** "feature" → "Feature". */
function capitalized(name: string): string {
  const text = name.trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : text;
}

/** The stored value as a name, when it is one rather than a number. */
function storedName(stored: unknown): string | null {
  if (typeof stored !== 'string') return null;
  const text = stored.trim();
  return text && !/^\d+$/.test(text) ? text : null;
}

/**
 * The tag for a stored altitude: the name that follows the number. Null when there is nothing to
 * name (no number and no stored name).
 */
export function altitudeTagOf(stored: unknown, category?: string | null, labels?: AltitudeLabelSet): string | null {
  const level = altitudeNumberOf(stored);
  const name = storedName(stored);
  if (level === null) return name ? capitalized(name) : null;
  const own = labels?.[level]?.trim();
  if (own) return own;
  const builtIn = category ? ALTITUDE_NAMES_BY_CATEGORY[category]?.[level] : undefined;
  if (builtIn) return builtIn;
  if (name) return capitalized(name);
  return ALTITUDE_LEVEL_NAMES[level];
}

export interface FormatAltitudeOptions {
  /** The item's category slug; names the level the way that category does. */
  category?: string | null;
  /** The workspace's own labels for that category (see altitudeLabelsFor). */
  labels?: AltitudeLabelSet;
  /**
   * Leave "Altitude" off ("3 · Feature"). For a control or column whose own label already says
   * Altitude; everywhere else the word goes with the number.
   */
  short?: boolean;
}

/**
 * "Altitude 3 · Feature" (or "3 · Feature" when `short`). A stored name with no number (theme,
 * subtask) is shown as its name alone; nothing stored at all is null, for the caller to say "Not set".
 */
export function formatAltitude(stored: unknown, options: FormatAltitudeOptions = {}): string | null {
  const level = altitudeNumberOf(stored);
  const tag = altitudeTagOf(stored, options.category, options.labels);
  if (level === null) return tag;
  const number = options.short ? `${level}` : `Altitude ${level}`;
  return tag ? `${number} · ${tag}` : number;
}

/** The workspace's labels for one category, picked from the item-categories list. */
export function altitudeLabelsFor(
  categories: ReadonlyArray<{ slug: string; altitudeLabels?: AltitudeLabelSet }> | null | undefined,
  slug: string | null | undefined,
): AltitudeLabelSet {
  if (!slug || !categories) return null;
  const labels = categories.find((c) => c.slug === slug)?.altitudeLabels;
  if (!labels) return null;
  return LEVELS.some((l) => labels[l]?.trim()) ? labels : null;
}
